import React, { useMemo, useState, Component } from 'react';
import { useRaceStore } from '../../stores/raceStore';
import { useTranslation } from '../../utils/i18n';
import { format } from 'date-fns';
import { MoonIcon, SunIcon, RouteIcon, TrendingUpIcon, TrendingDownIcon, FlagIcon, TimerIcon, ShieldCheckIcon, AlertCircleIcon, MountainIcon, ArrowRightIcon, CoffeeIcon, ZapIcon, ThermometerIcon, GaugeIcon, ChevronDownIcon, ChevronUpIcon, FlagTriangleRightIcon, MapPinIcon, TentIcon, UtensilsIcon, DropletIcon, CircleDotIcon, ClockIcon } from 'lucide-react';
import { CheckpointType, TerrainType, ExecutionScenario } from '../../types';
import { PacingElevationProfile } from './PacingElevationProfile';
import { AVAILABLE_MODELS } from '../../utils/pacingModels';
import { trackPacingModelChanged, trackExecutionScenarioChanged } from '../../utils/analytics';
// --- Constants ---
const CHECKPOINT_CONFIG: Record<CheckpointType, {
  color: string;
  bg: string;
  border: string;
  icon: React.ElementType;
  labelKey: string;
}> = {
  None: {
    color: '#9B9BA7',
    bg: 'bg-[#F4F4F5]',
    border: 'border-[#E4E4E7]',
    icon: CircleDotIcon,
    labelKey: 'checkpoint.short.None'
  },
  BV: {
    color: '#0666EB',
    bg: 'bg-[#EFF6FF]',
    border: 'border-[#DBEAFE]',
    icon: TentIcon,
    labelKey: 'checkpoint.short.BV'
  },
  Aid: {
    color: '#00B843',
    bg: 'bg-[#ECFDF5]',
    border: 'border-[#D1FAE5]',
    icon: UtensilsIcon,
    labelKey: 'checkpoint.short.Aid'
  },
  Water: {
    color: '#0EA5E9',
    bg: 'bg-[#F0F9FF]',
    border: 'border-[#E0F2FE]',
    icon: DropletIcon,
    labelKey: 'checkpoint.short.Water'
  },
  Other: {
    color: '#F59E0B',
    bg: 'bg-[#FFFBEB]',
    border: 'border-[#FEF3C7]',
    icon: MapPinIcon,
    labelKey: 'checkpoint.short.Other'
  }
};
const TERRAIN_COLORS: Record<TerrainType, string> = {
  Road: '#6B6B76',
  SmoothTrail: '#00B843',
  MixedTrail: '#FF9500',
  Rocky: '#E34402',
  Technical: '#C93D00',
  MudSand: '#8B6914',
  OfftrailSteep: '#7B61FF'
};
const BUFFER_STYLES = {
  green: {
    bg: 'bg-[#00B843]/10',
    text: 'text-[#00B843]',
    border: 'border-[#00B843]/20'
  },
  yellow: {
    bg: 'bg-[#FF9500]/10',
    text: 'text-[#FF9500]',
    border: 'border-[#FF9500]/20'
  },
  red: {
    bg: 'bg-[#E34402]/10',
    text: 'text-[#E34402]',
    border: 'border-[#E34402]/20'
  }
};
// --- Components ---
function PacingSummaryBar({
  count,
  totalTime,
  finishEta,
  scenario
}: {
  count: number;
  totalTime: string;
  finishEta?: Date;
  scenario: string;
}) {
  const {
    t
  } = useTranslation();
  return <div className="flex items-center gap-2 flex-wrap px-1" data-id="element-699">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]" data-id="element-700">
        <MapPinIcon className="w-3 h-3" data-id="element-701" />
        <span data-id="element-702">
          {t('pacing.segments', {
          count
        })}
        </span>
      </div>

      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold bg-[#FC4C02]/10 text-[#FC4C02] border-[#FC4C02]/20" data-id="element-703">
        <TimerIcon className="w-3 h-3" data-id="element-704" />
        <span data-id="element-705">{totalTime}</span>
      </div>

      {finishEta && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold bg-[#FFFBEB] text-[#F59E0B] border-[#FEF3C7]" data-id="element-706">
          <FlagIcon className="w-3 h-3" data-id="element-707" />
          <span data-id="element-708">{format(finishEta, 'HH:mm dd/MM')}</span>
        </div>}

      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold bg-[#F0F9FF] text-[#0EA5E9] border-[#E0F2FE] capitalize" data-id="element-709">
        <ZapIcon className="w-3 h-3" data-id="element-710" />
        <span data-id="element-711">{t(`scenario.${scenario}`)}</span>
      </div>
    </div>;
}
function PacingRow({
  segment,
  result,
  index,
  isLast,
  accumulatedDistance,
  isExpanded,
  onClick,
  bufferStatus
}: {
  segment: any;
  result: any;
  index: number;
  isLast: boolean;
  accumulatedDistance: number;
  isExpanded: boolean;
  onClick: () => void;
  bufferStatus?: 'green' | 'yellow' | 'red';
}) {
  const {
    t
  } = useTranslation();
  const config = CHECKPOINT_CONFIG[segment.checkpointType as CheckpointType] || CHECKPOINT_CONFIG.None;
  const Icon = config.icon;
  const formatPace = (minPerKm: number) => {
    const m = Math.floor(minPerKm);
    const s = Math.round((minPerKm - m) * 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const formatDuration = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h${m.toString().padStart(2, '0')}m`;
  };
  const formatAccumulated = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };
  return <button onClick={onClick} className={`w-full bg-white border p-3 lg:p-4 flex items-start lg:items-center gap-3 lg:gap-4 hover:border-[#FC4C02]/30 transition-all text-left group shadow-sm ${isExpanded ? 'rounded-t-xl border-[#FC4C02]/20 border-b-0 z-10 relative' : 'rounded-xl border-[#E8E8ED]'} ${isLast ? 'bg-gradient-to-r from-[#FFFBEB]/40 to-transparent' : ''}`} data-id="element-712">
      {/* Index / Icon */}
      <div className={`w-9 h-9 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors border mt-0.5 lg:mt-0 ${isExpanded ? 'bg-[#FC4C02] border-[#FC4C02] text-white' : isLast ? 'bg-[#FC4C02]/10 border-[#FC4C02]/20 text-[#FC4C02]' : 'bg-[#F7F7FA] border-[#E8E8ED] text-[#6B6B76] group-hover:border-[#FC4C02]/30'}`} data-id="element-713">
        {isLast ? <FlagTriangleRightIcon className="w-4 h-4 lg:w-5 lg:h-5" data-id="element-714" /> : <span className="text-xs lg:text-sm font-bold" data-id="element-715">{index}</span>}
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0" data-id="element-716">
        {/* Mobile: Name + Chevron on first line */}
        <div className="flex items-center justify-between gap-2 mb-1" data-id="element-717">
          <span className={`text-[13px] lg:text-sm font-semibold text-[#2D2D2D] truncate ${isLast ? 'font-bold' : ''}`} data-id="element-718">
            {segment.name}
          </span>

          {/* Desktop tags */}
          <div className="hidden lg:flex items-center gap-1.5 flex-shrink-0" data-id="element-719">
            {segment.checkpointType !== 'None' && <div className={`px-2 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1 ${config.bg} ${config.border}`} data-id="element-720">
                <Icon className="w-3 h-3" style={{
              color: config.color
            }} data-id="element-721" />
                <span style={{
              color: config.color
            }} data-id="element-722">
                  {t(config.labelKey)}
                </span>
              </div>}
            <div className="px-2 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1 bg-[#F7F7FA] border-[#E8E8ED]" data-id="element-723">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
              backgroundColor: TERRAIN_COLORS[segment.terrainType as TerrainType || 'SmoothTrail']
            }} data-id="element-724" />
              <span className="text-[#6B6B76]" data-id="element-725">
                {t(`terrain.${segment.terrainType || 'SmoothTrail'}`)}
              </span>
            </div>
          </div>

          {/* Mobile chevron (top-right) */}
          <div className={`lg:hidden transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-180 text-[#FC4C02]' : 'text-[#D4D4D8]'}`} data-id="element-726">
            <ChevronDownIcon className="w-4 h-4" data-id="element-727" />
          </div>
        </div>

        {/* Mobile tags row */}
        <div className="flex lg:hidden items-center gap-1.5 mb-1.5 flex-wrap" data-id="element-728">
          {segment.checkpointType !== 'None' && <div className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold border flex items-center gap-0.5 ${config.bg} ${config.border}`} data-id="element-729">
              <Icon className="w-2.5 h-2.5" style={{
            color: config.color
          }} data-id="element-730" />
              <span style={{
            color: config.color
          }} data-id="element-731">
                {t(config.labelKey)}
              </span>
            </div>}
          <div className="px-1.5 py-0.5 rounded-full text-[9px] font-bold border flex items-center gap-0.5 bg-[#F7F7FA] border-[#E8E8ED]" data-id="element-732">
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
            backgroundColor: TERRAIN_COLORS[segment.terrainType as TerrainType || 'SmoothTrail']
          }} data-id="element-733" />
            <span className="text-[#6B6B76]" data-id="element-734">
              {t(`terrain.${segment.terrainType || 'SmoothTrail'}`)}
            </span>
          </div>
        </div>

        {/* Stats + Accumulated time row */}
        <div className="flex items-center justify-between gap-2" data-id="element-735">
          {/* Left: distance, elevation, segment time, pace */}
          <div className="flex items-center gap-2 lg:gap-3 text-[11px] lg:text-xs text-[#9B9BA7] min-w-0 flex-wrap" data-id="element-736">
            <span className="flex items-center gap-0.5 lg:gap-1 font-medium text-[#6B6B76] whitespace-nowrap" data-id="element-737">
              <RouteIcon className="w-3 h-3 flex-shrink-0" data-id="element-738" />
              {segment.distance.toFixed(1)}km
            </span>
            <span className="w-0.5 h-3 bg-[#E8E8ED] flex-shrink-0" data-id="element-739" />
            <span className="flex items-center gap-0.5 whitespace-nowrap" data-id="element-740">
              <TrendingUpIcon className="w-3 h-3 flex-shrink-0" data-id="element-741" />+
              {Math.round(segment.elevationGain)}m
            </span>
            <span className="flex items-center gap-0.5 whitespace-nowrap" data-id="element-742">
              <TrendingDownIcon className="w-3 h-3 flex-shrink-0" data-id="element-743" />-
              {Math.round(segment.elevationLoss)}m
            </span>
            <span className="w-0.5 h-3 bg-[#E8E8ED] flex-shrink-0" data-id="element-744" />
            <span className="flex items-center gap-0.5 whitespace-nowrap font-medium text-[#6B6B76]" data-id="element-745">
              <TimerIcon className="w-3 h-3 flex-shrink-0" data-id="element-746" />
              {formatDuration(result.duration + segment.stopTime)}
            </span>
            <span className="whitespace-nowrap tabular-nums" data-id="element-747">
              {formatPace(result.pace)}/km
            </span>
          </div>

          {/* Right: Accumulated time + ETA (mobile) */}
          <div className="flex lg:hidden items-center gap-2 flex-shrink-0" data-id="element-748">
            <div className="flex flex-col items-end" data-id="element-749">
              <span className="text-[13px] font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-750">
                {formatAccumulated(result.accumulatedTime)}
              </span>
              <div className="flex items-center gap-1" data-id="element-751">
                <span className="text-[10px] font-semibold text-[#6B6B76] tabular-nums" data-id="element-752">
                  {format(result.eta, 'HH:mm')}
                </span>
                {result.isNight ? <MoonIcon className="w-2.5 h-2.5 text-[#7B61FF]" data-id="element-753" /> : <SunIcon className="w-2.5 h-2.5 text-[#FF9500]" data-id="element-754" />}
              </div>
            </div>
            {bufferStatus && bufferStatus !== 'green' && <div className={`w-2 h-2 rounded-full flex-shrink-0 ${bufferStatus === 'yellow' ? 'bg-[#FF9500]' : 'bg-[#E34402]'}`} data-id="element-755" />}
          </div>
        </div>
      </div>

      {/* Desktop Right Side: Accumulated Time, ETA & Chevron */}
      <div className="hidden lg:flex items-center gap-3 pl-2 border-l border-[#F0F0F5]" data-id="element-756">
        <div className="flex flex-col items-end" data-id="element-757">
          <div className="flex items-center gap-1.5" data-id="element-758">
            <span className="text-sm font-bold text-[#2D2D2D] tabular-nums" data-id="element-759">
              {formatAccumulated(result.accumulatedTime)}
            </span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-760">
            <span className="text-xs font-semibold text-[#6B6B76] tabular-nums" data-id="element-761">
              {format(result.eta, 'HH:mm')}
            </span>
            {result.isNight ? <MoonIcon className="w-3 h-3 text-[#7B61FF]" data-id="element-762" /> : <SunIcon className="w-3 h-3 text-[#FF9500]" data-id="element-763" />}
          </div>
        </div>

        {bufferStatus && <div className={`w-2 h-2 rounded-full ${bufferStatus === 'green' ? 'bg-[#00B843]' : bufferStatus === 'yellow' ? 'bg-[#FF9500]' : 'bg-[#E34402]'}`} data-id="element-764" />}

        <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180 text-[#FC4C02]' : 'text-[#D4D4D8] group-hover:text-[#FC4C02]'}`} data-id="element-765">
          <ChevronDownIcon className="w-5 h-5" data-id="element-766" />
        </div>
      </div>
    </button>;
}
function PacingDetailPanel({
  segment,
  result,
  buffer,
  badges,
  onUpdatePace,
  onUpdateStop
}: {
  segment: any;
  result: any;
  buffer: any;
  badges: any;
  onUpdatePace: (pace: string) => void;
  onUpdateStop: (mins: number) => void;
}) {
  const {
    t
  } = useTranslation();
  const formatPace = (minPerKm: number) => {
    const m = Math.floor(minPerKm);
    const s = Math.round((minPerKm - m) * 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const [paceInput, setPaceInput] = useState(formatPace(result.pace));
  const [stopInput, setStopInput] = useState(segment.stopTime.toString());
  const [paceError, setPaceError] = useState(false);
  const [stopError, setStopError] = useState(false);
  const validateAndParsePace = (val: string): number | null => {
    // Accept M:SS or just a number
    const trimmed = val.trim();
    if (!trimmed) return null;
    // Try M:SS format
    const colonMatch = trimmed.match(/^(\d{1,2}):(\d{1,2})$/);
    if (colonMatch) {
      const m = parseInt(colonMatch[1]);
      const s = parseInt(colonMatch[2]);
      if (s >= 60) return null;
      const pace = m + s / 60;
      if (pace < 2 || pace > 40) return null;
      return pace;
    }
    // Try plain decimal (e.g. "6.5")
    const num = parseFloat(trimmed);
    if (!isNaN(num) && num >= 2 && num <= 40) return num;
    return null;
  };
  const handlePaceChange = (val: string) => {
    setPaceInput(val);
    setPaceError(false);
  };
  const handlePaceBlur = () => {
    const parsed = validateAndParsePace(paceInput);
    if (parsed !== null) {
      setPaceError(false);
      // Normalize display to M:SS
      const m = Math.floor(parsed);
      const s = Math.round((parsed - m) * 60);
      setPaceInput(`${m}:${s.toString().padStart(2, '0')}`);
      onUpdatePace(`${m}:${s.toString().padStart(2, '0')}`);
    } else {
      setPaceError(true);
    }
  };
  const handlePaceKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      ;
      (e.target as HTMLInputElement).blur();
    }
  };
  const handleStopChange = (val: string) => {
    setStopInput(val);
    setStopError(false);
    const num = parseFloat(val);
    if (!isNaN(num) && num >= 0 && num <= 120) {
      onUpdateStop(num);
    }
  };
  const handleStopBlur = () => {
    const num = parseFloat(stopInput);
    if (isNaN(num) || num < 0 || num > 120) {
      setStopError(true);
    } else {
      setStopError(false);
      const clamped = Math.min(120, Math.max(0, Math.round(num)));
      setStopInput(clamped.toString());
      onUpdateStop(clamped);
    }
  };
  const formatDuration = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h ${m.toString().padStart(2, '0')}m`;
  };
  const formatBuffer = (mins: number) => {
    const sign = mins >= 0 ? '+' : '-';
    const abs = Math.abs(mins);
    const h = Math.floor(abs / 60);
    const m = Math.round(abs % 60);
    return `${sign}${h}h${m.toString().padStart(2, '0')}m`;
  };
  return <div className="bg-[#FAFAFA] rounded-b-xl border border-t-0 border-[#E8E8ED] -mt-1 overflow-hidden animate-in slide-in-from-top-2 duration-200" data-id="element-767">
      <div className="p-4 space-y-4" data-id="element-768">
        {/* Editable Inputs */}
        <div className="grid grid-cols-2 gap-4" data-id="element-769">
          <div data-id="element-770">
            <label className="block text-[10px] font-bold text-[#9B9BA7] uppercase tracking-wider mb-1.5" data-id="element-771">
              {t('pacing.effortPace')}
            </label>
            <div className="relative" data-id="element-772">
              <input type="text" value={paceInput} onChange={e => handlePaceChange(e.target.value)} onBlur={handlePaceBlur} onKeyDown={handlePaceKeyDown} className={`w-full h-10 px-3 bg-white border rounded-xl text-sm font-bold text-[#2D2D2D] focus:ring-2 focus:border-transparent transition-all tabular-nums ${paceError ? 'border-[#E34402] focus:ring-[#E34402] bg-[#FEF2F2]' : 'border-[#E8E8ED] focus:ring-[#FC4C02]'}`} placeholder="M:SS" data-id="element-773" />
              <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium ${paceError ? 'text-[#E34402]' : 'text-[#9B9BA7]'}`} data-id="element-774">
                min/km
              </span>
            </div>
            {paceError && <p className="text-[10px] text-[#E34402] font-medium mt-1" data-id="element-775">
                2:00 – 40:00 min/km
              </p>}
          </div>

          <div data-id="element-776">
            <label className="block text-[10px] font-bold text-[#9B9BA7] uppercase tracking-wider mb-1.5" data-id="element-777">
              {t('pacing.stopTime')}
            </label>
            <div className="relative" data-id="element-778">
              <input type="number" min="0" max="120" value={stopInput} onChange={e => handleStopChange(e.target.value)} onBlur={handleStopBlur} className={`w-full h-10 px-3 bg-white border rounded-xl text-sm font-bold text-[#2D2D2D] focus:ring-2 focus:border-transparent transition-all tabular-nums ${stopError ? 'border-[#E34402] focus:ring-[#E34402] bg-[#FEF2F2]' : 'border-[#E8E8ED] focus:ring-[#FC4C02]'}`} data-id="element-779" />
              <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium ${stopError ? 'text-[#E34402]' : 'text-[#9B9BA7]'}`} data-id="element-780">
                min
              </span>
            </div>
            {stopError && <p className="text-[10px] text-[#E34402] font-medium mt-1" data-id="element-781">
                0 – 120 min
              </p>}
          </div>
        </div>

        {/* Read-only Stats */}
        <div className="grid grid-cols-3 gap-2 bg-white rounded-xl border border-[#E8E8ED] p-3" data-id="element-782">
          <div className="text-center" data-id="element-783">
            <div className="text-[10px] font-medium text-[#9B9BA7] uppercase mb-0.5" data-id="element-784">
              {t('pacing.segment')}
            </div>
            <div className="text-sm font-bold text-[#2D2D2D] tabular-nums" data-id="element-785">
              {formatDuration(result.duration + segment.stopTime)}
            </div>
          </div>
          <div className="text-center border-l border-[#F0F0F5]" data-id="element-786">
            <div className="text-[10px] font-medium text-[#9B9BA7] uppercase mb-0.5" data-id="element-787">
              {t('pacing.eta')}
            </div>
            <div className="text-sm font-bold text-[#2D2D2D] tabular-nums" data-id="element-788">
              {format(result.eta, 'HH:mm')}
            </div>
          </div>
          <div className="text-center border-l border-[#F0F0F5]" data-id="element-789">
            <div className="text-[10px] font-medium text-[#9B9BA7] uppercase mb-0.5" data-id="element-790">
              {t('pacing.accumulated')}
            </div>
            <div className="text-sm font-bold text-[#2D2D2D] tabular-nums" data-id="element-791">
              {formatDuration(result.accumulatedTime)}
            </div>
          </div>
        </div>

        {/* Badges & Info */}
        <div className="flex flex-wrap gap-2 items-center" data-id="element-792">
          {/* Buffer */}
          {buffer && <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-bold ${BUFFER_STYLES[buffer.status as 'green' | 'yellow' | 'red'].bg} ${BUFFER_STYLES[buffer.status as 'green' | 'yellow' | 'red'].text} ${BUFFER_STYLES[buffer.status as 'green' | 'yellow' | 'red'].border}`} data-id="element-793">
              <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-794" />
              <span data-id="element-795">
                {t('pacing.buffer')}: {formatBuffer(buffer.bufferMinutes)}
              </span>
            </div>}

          {/* Climate */}
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-bold ${result.isNight ? 'bg-[#7B61FF]/10 text-[#7B61FF] border-[#7B61FF]/20' : 'bg-[#FF9500]/10 text-[#FF9500] border-[#FF9500]/20'}`} data-id="element-796">
            {result.isNight ? <MoonIcon className="w-3.5 h-3.5" data-id="element-797" /> : <SunIcon className="w-3.5 h-3.5" data-id="element-798" />}
            <span data-id="element-799">{result.isNight ? t('pacing.night') : t('pacing.day')}</span>
          </div>

          {(result.climate.heatIndex === 'hot' || result.climate.heatIndex === 'warm') && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-bold bg-[#E34402]/10 text-[#E34402] border-[#E34402]/20" data-id="element-800">
              <ThermometerIcon className="w-3.5 h-3.5" data-id="element-801" />
              <span data-id="element-802">{t('pacing.hot')}</span>
            </div>}

          {/* Strategic Badges */}
          {badges.showBaseCamp && <span className="text-[10px] font-bold text-[#0666EB] bg-[#0666EB]/10 px-2 py-1 rounded-lg border border-[#0666EB]/20" data-id="element-803">
              {t('pacing.baseCamp')}
            </span>}
          {badges.showLong && <span className="text-[10px] font-bold text-[#6B6B76] bg-[#F7F7FA] px-2 py-1 rounded-lg border border-[#E8E8ED] flex items-center gap-1" data-id="element-804">
              <ArrowRightIcon className="h-3 w-3" data-id="element-805" /> {t('pacing.long')}
            </span>}
          {badges.showSteep && <span className="text-[10px] font-bold text-[#E34402] bg-[#E34402]/10 px-2 py-1 rounded-lg border border-[#E34402]/20 flex items-center gap-1" data-id="element-806">
              <MountainIcon className="h-3 w-3" data-id="element-807" /> {t('pacing.steep')}
            </span>}
        </div>
      </div>
    </div>;
}
// --- Main Component ---
export function PacingTable() {
  const {
    t
  } = useTranslation();
  const {
    segments,
    pacingResults,
    gpxData,
    settings,
    updateSegment,
    updateSegmentPace,
    updateSettings
  } = useRaceStore();
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [elevationExpanded, setElevationExpanded] = useState(true);
  const [strategyExpanded, setStrategyExpanded] = useState(true);
  if (pacingResults.length === 0 || !gpxData) return null;
  // Compute accumulated distances
  const accumulatedDistances = segments.reduce<number[]>((acc, seg, i) => {
    const prev = i > 0 ? acc[i - 1] : 0;
    acc.push(prev + seg.distance);
    return acc;
  }, []);
  // --- Helpers ---
  const formatDuration = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h ${m.toString().padStart(2, '0')}m`;
  };
  const parsePace = (value: string): number | undefined => {
    if (!value) return undefined;
    const parts = value.split(':');
    const m = parseInt(parts[0]);
    const s = parseInt(parts[1] || '0');
    if (isNaN(m)) return undefined;
    return m + s / 60;
  };
  const lastResult = pacingResults[pacingResults.length - 1];
  const totalTime = lastResult?.accumulatedTime || 0;
  const finishEta = lastResult?.eta;
  const getCutoffBuffer = (segmentIndex: number) => {
    const segment = segments[segmentIndex];
    const result = pacingResults[segmentIndex];
    if (!segment || !result || !segment.cutoffMinutes || segment.cutoffMinutes <= 0) return null;
    const bufferMinutes = segment.cutoffMinutes - result.accumulatedTime;
    const bufferPct = bufferMinutes / segment.cutoffMinutes * 100;
    let status: 'green' | 'yellow' | 'red';
    if (bufferMinutes <= 0) {
      status = 'red';
    } else if (bufferPct <= 10) {
      status = 'yellow';
    } else {
      status = 'green';
    }
    return {
      bufferMinutes,
      bufferPct,
      status
    };
  };
  // --- Strategic Badge Logic ---
  const longSegmentIds = useMemo(() => {
    if (segments.length === 0) return new Set<string>();
    const avgDist = segments.reduce((s, seg) => s + seg.distance, 0) / segments.length;
    const threshold120 = avgDist * 1.2;
    const candidates = segments.filter(s => s.distance >= 10 && s.distance >= threshold120).sort((a, b) => b.distance - a.distance);
    const top2 = [...segments].sort((a, b) => b.distance - a.distance).slice(0, 2).filter(s => s.distance >= 10);
    const merged = new Map<string, number>();
    for (const s of [...candidates, ...top2]) {
      merged.set(s.id, s.distance);
    }
    const sorted = [...merged.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
    return new Set(sorted.map(([id]) => id));
  }, [segments]);
  const steepSegmentIds = useMemo(() => {
    if (segments.length === 0) return new Set<string>();
    return new Set(segments.filter(s => {
      if (s.terrainType === 'Road' || s.distance <= 0) return false;
      const verticalDensity = s.elevationGain / s.distance;
      return verticalDensity >= 60;
    }).map(s => s.id));
  }, [segments]);
  const baseCampSegmentId = useMemo(() => {
    const first = segments.find(s => s.checkpointType === 'BV');
    return first?.id || null;
  }, [segments]);
  const nightTransitionSegmentId = useMemo(() => {
    for (let i = 0; i < pacingResults.length; i++) {
      if (pacingResults[i].isNight && (i === 0 || !pacingResults[i - 1].isNight)) {
        return pacingResults[i].segmentId;
      }
    }
    return null;
  }, [pacingResults]);
  const handleToggle = (id: string) => {
    setSelectedSegmentId(prev => prev === id ? null : id);
  };
  return <div className="space-y-6" data-id="element-808">
      {/* Strategy Selector Bar */}
      <div className="bg-white rounded-2xl border border-[#E8E8ED] p-3 sm:p-4 lg:p-5 shadow-sm" data-id="element-809">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-810">
          {/* Pacing Model */}
          <div className="flex flex-col gap-3" data-id="element-811">
            <div className="flex items-center gap-2" data-id="element-812">
              <GaugeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FC4C02]" data-id="element-813" />
              <span className="font-bold text-[#2D2D2D] text-sm" data-id="element-814">
                {'Pacing Model'}
              </span>
              {settings.activeModelId === 'custom' && <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-[#FC4C02]/10 text-[#FC4C02] text-[10px] font-bold border border-[#FC4C02]/20" data-id="element-815">
                  {t('pacing.custom')}
                </span>}
            </div>
            <div className="relative" data-id="element-816">
              <select value={settings.activeModelId} onChange={e => {
              const newId = e.target.value;
              trackPacingModelChanged(newId);
              if (newId !== 'custom') {
                updateSettings({
                  activeModelId: newId,
                  previousModelId: undefined
                });
              } else {
                updateSettings({
                  activeModelId: newId
                });
              }
            }} className="w-full appearance-none h-12 bg-[#F7F7FA] border border-[#E8E8ED] text-[#2D2D2D] text-[15px] font-medium rounded-xl px-4 pr-10 focus:ring-2 focus:ring-[#FC4C02] focus:border-transparent cursor-pointer" data-id="element-817">
                {AVAILABLE_MODELS.map(model => <option key={model.id} value={model.id} data-id="element-818">
                    {model.name}
                  </option>)}
                {settings.activeModelId === 'custom' && <option value="custom" data-id="element-819">{t('pacing.customOverridden')}</option>}
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-820">
                <ChevronDownIcon className="w-4 h-4 text-[#9B9BA7]" data-id="element-821" />
              </div>
            </div>
          </div>

          {/* Execution Strategy */}
          <div className="flex flex-col gap-3" data-id="element-822">
            <div className="flex items-center gap-2" data-id="element-823">
              <ZapIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FC4C02]" data-id="element-824" />
              <span className="font-bold text-[#2D2D2D] text-sm" data-id="element-825">
                {'Execution Strategy'}
              </span>
            </div>
            <div className="flex bg-[#F7F7FA] p-1 rounded-xl border border-[#E8E8ED] h-12 items-center" data-id="element-826">
              {(['optimal', 'standard', 'safe'] as ExecutionScenario[]).map(scenario => <button key={scenario} onClick={() => {
              trackExecutionScenarioChanged(scenario);
              updateSettings({
                executionScenario: scenario
              });
            }} className={`
                          flex-1 h-full rounded-lg text-sm font-semibold transition-all whitespace-nowrap text-center
                          ${settings.executionScenario === scenario ? 'bg-white text-[#2D2D2D] shadow-sm border border-[#E8E8ED]' : 'text-[#9B9BA7] hover:text-[#6B6B76]'}
                      `} data-id="element-827">
                    {t(`scenario.${scenario}`)}
                  </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Header */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-828">
        {/* Total Time */}
        <div className="hidden lg:flex bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4 items-center gap-4" data-id="element-829">
          <div className="w-11 h-11 rounded-xl bg-[#FC4C02]/10 flex items-center justify-center flex-shrink-0" data-id="element-830">
            <TimerIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-831" />
          </div>
          <div data-id="element-832">
            <div className="text-[11px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-833">
              {t('pacing.totalTime')}
            </div>
            <div className="text-2xl font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-834">
              {formatDuration(totalTime)}
            </div>
          </div>
        </div>
        {/* Distance — desktop only */}
        <div className="hidden lg:flex bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4 items-center gap-4" data-id="element-835">
          <div className="w-11 h-11 rounded-xl bg-[#FC4C02]/10 flex items-center justify-center flex-shrink-0" data-id="element-836">
            <RouteIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-837" />
          </div>
          <div data-id="element-838">
            <div className="text-[11px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-839">
              {t('course.distance')}
            </div>
            <div className="text-2xl font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-840">
              {gpxData.totalDistance.toFixed(1)}
              <span className="text-sm font-medium text-[#9B9BA7] ml-0.5" data-id="element-841">
                km
              </span>
            </div>
          </div>
        </div>

        {/* Elevation+ — desktop only */}
        <div className="hidden lg:flex bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4 items-center gap-4" data-id="element-842">
          <div className="w-11 h-11 rounded-xl bg-[#00B843]/10 flex items-center justify-center flex-shrink-0" data-id="element-843">
            <TrendingUpIcon className="h-5 w-5 text-[#00B843]" data-id="element-844" />
          </div>
          <div data-id="element-845">
            <div className="text-[11px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-846">
              {t('course.elevationPlus')}
            </div>
            <div className="text-2xl font-bold text-[#00B843] tabular-nums leading-tight" data-id="element-847">
              {Math.round(gpxData.totalElevationGain)}
              <span className="text-sm font-medium text-[#9B9BA7] ml-0.5" data-id="element-848">
                m
              </span>
            </div>
          </div>
        </div>

        {/* Elevation- — desktop only */}
        <div className="hidden lg:flex bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4 items-center gap-4" data-id="element-849">
          <div className="w-11 h-11 rounded-xl bg-[#E34402]/10 flex items-center justify-center flex-shrink-0" data-id="element-850">
            <TrendingUpIcon className="h-5 w-5 text-[#E34402] rotate-180" data-id="element-851" />
          </div>
          <div data-id="element-852">
            <div className="text-[11px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-853">
              {t('course.elevationMinus')}
            </div>
            <div className="text-2xl font-bold text-[#E34402] tabular-nums leading-tight" data-id="element-854">
              {Math.round(gpxData.totalElevationLoss)}
              <span className="text-sm font-medium text-[#9B9BA7] ml-0.5" data-id="element-855">
                m
              </span>
            </div>
          </div>
        </div>

        {/* Finish — desktop only */}
        <div className="hidden lg:flex bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4 items-center gap-4" data-id="element-856">
          <div className="w-11 h-11 rounded-xl bg-[#FF9500]/10 flex items-center justify-center flex-shrink-0" data-id="element-857">
            <FlagIcon className="h-5 w-5 text-[#FF9500]" data-id="element-858" />
          </div>
          <div data-id="element-859">
            <div className="text-[11px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-860">
              {t('pacing.finish')}
            </div>
            {finishEta && <div data-id="element-861">
                <div className="text-2xl font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-862">
                  {format(finishEta, 'HH:mm')}
                </div>
                <div className="text-xs font-medium text-[#9B9BA7]" data-id="element-863">
                  {format(finishEta, 'dd/MM/yyyy')}
                </div>
              </div>}
          </div>
        </div>

        {/* Mobile summary card */}
        <div className="lg:hidden col-span-2 bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4" data-id="element-864">
          {/* Total Time */}
          <div className="flex items-center gap-3" data-id="element-865">
            <div className="w-9 h-9 rounded-lg bg-[#FC4C02]/10 flex items-center justify-center flex-shrink-0" data-id="element-866">
              <TimerIcon className="h-4 w-4 text-[#FC4C02]" data-id="element-867" />
            </div>
            <div data-id="element-868">
              <div className="text-[10px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-869">
                {t('pacing.totalTime')}
              </div>
              <div className="text-xl font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-870">
                {formatDuration(totalTime)}
              </div>
            </div>
          </div>
          {/* Distance / Finish */}
          <div className="border-t border-[#F0F0F5] mt-3 pt-3 grid grid-cols-2 gap-3" data-id="element-871">
            <div className="flex items-center gap-2.5" data-id="element-872">
              <div className="w-8 h-8 rounded-lg bg-[#FC4C02]/10 flex items-center justify-center flex-shrink-0" data-id="element-873">
                <RouteIcon className="h-3.5 w-3.5 text-[#FC4C02]" data-id="element-874" />
              </div>
              <div data-id="element-875">
                <div className="text-[10px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-876">
                  {t('course.distance')}
                </div>
                <div className="text-base font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-877">
                  {gpxData.totalDistance.toFixed(1)}
                  <span className="text-[10px] font-medium text-[#9B9BA7] ml-0.5" data-id="element-878">
                    km
                  </span>
                </div>
              </div>
            </div>
            {finishEta && <div className="flex items-center gap-2.5" data-id="element-879">
                <div className="w-8 h-8 rounded-lg bg-[#FF9500]/10 flex items-center justify-center flex-shrink-0" data-id="element-880">
                  <FlagIcon className="h-3.5 w-3.5 text-[#FF9500]" data-id="element-881" />
                </div>
                <div data-id="element-882">
                  <div className="text-[10px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-883">
                    {t('pacing.finish')}
                  </div>
                  <div className="text-base font-bold text-[#FF9500] tabular-nums leading-tight" data-id="element-884">
                    {format(finishEta, 'HH:mm')}
                    <span className="text-[10px] font-medium text-[#9B9BA7] ml-1" data-id="element-885">
                      {format(finishEta, 'dd/MM')}
                    </span>
                  </div>
                </div>
              </div>}
          </div>
          {/* D+ / D- */}
          <div className="border-t border-[#F0F0F5] mt-3 pt-3 grid grid-cols-2 gap-3" data-id="element-886">
            <div className="flex items-center gap-2.5" data-id="element-887">
              <div className="w-8 h-8 rounded-lg bg-[#00B843]/10 flex items-center justify-center flex-shrink-0" data-id="element-888">
                <TrendingUpIcon className="h-3.5 w-3.5 text-[#00B843]" data-id="element-889" />
              </div>
              <div data-id="element-890">
                <div className="text-[10px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-891">
                  D+
                </div>
                <div className="text-base font-bold text-[#00B843] tabular-nums leading-tight" data-id="element-892">
                  {Math.round(gpxData.totalElevationGain)}
                  <span className="text-[10px] font-medium text-[#9B9BA7] ml-0.5" data-id="element-893">
                    m
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2.5" data-id="element-894">
              <div className="w-8 h-8 rounded-lg bg-[#E34402]/10 flex items-center justify-center flex-shrink-0" data-id="element-895">
                <TrendingUpIcon className="h-3.5 w-3.5 text-[#E34402] rotate-180" data-id="element-896" />
              </div>
              <div data-id="element-897">
                <div className="text-[10px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-898">
                  D-
                </div>
                <div className="text-base font-bold text-[#E34402] tabular-nums leading-tight" data-id="element-899">
                  {Math.round(gpxData.totalElevationLoss)}
                  <span className="text-[10px] font-medium text-[#9B9BA7] ml-0.5" data-id="element-900">
                    m
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enriched Elevation Profile — Collapsible, open by default */}
      <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm overflow-hidden" data-id="element-901">
        <button onClick={() => setElevationExpanded(!elevationExpanded)} className="w-full flex items-center justify-between px-5 py-3.5 text-base font-bold text-[#2D2D2D] hover:bg-[#FAFAFA] transition-colors" data-id="element-902">
          <span className="flex items-center gap-2.5" data-id="element-903">
            <TrendingUpIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-904" />
            {t('course.elevationProfile')}
          </span>
          {elevationExpanded ? <ChevronUpIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-905" /> : <ChevronDownIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-906" />}
        </button>
        {elevationExpanded && <div className="border-t border-[#E8E8ED]" data-id="element-907">
            <PacingElevationProfile data-id="element-908" />
          </div>}
      </div>

      {/* Pacing Strategy — Collapsible, open by default */}
      <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm overflow-hidden" data-id="element-909">
        <button onClick={() => setStrategyExpanded(!strategyExpanded)} className="w-full flex items-center justify-between px-5 py-3.5 text-base font-bold text-[#2D2D2D] hover:bg-[#FAFAFA] transition-colors" data-id="element-910">
          <span className="flex items-center gap-2.5" data-id="element-911">
            <TimerIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-912" />
            {t('pacing.title')}
          </span>
          {strategyExpanded ? <ChevronUpIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-913" /> : <ChevronDownIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-914" />}
        </button>
        {strategyExpanded && <div className="border-t border-[#E8E8ED] p-4" data-id="element-915">
            <div className="space-y-6 pb-20" data-id="element-916">
              {/* Section Header */}
              <div className="space-y-3" data-id="element-917">
                {/* Summary Bar */}
                <PacingSummaryBar count={segments.length} totalTime={formatDuration(totalTime)} finishEta={finishEta} scenario={settings.executionScenario} data-id="element-918" />

                {/* Rows */}
                {pacingResults.map((result, index) => {
              const segment = segments.find(s => s.id === result.segmentId);
              if (!segment) return null;
              const isLast = index === segments.length - 1;
              const isExpanded = selectedSegmentId === segment.id;
              const buffer = getCutoffBuffer(index);
              const badges = {
                showBaseCamp: baseCampSegmentId === segment.id,
                showLong: longSegmentIds.has(segment.id),
                showSteep: steepSegmentIds.has(segment.id),
                showNightTransition: nightTransitionSegmentId === segment.id
              };
              return <div key={segment.id} data-id="element-919">
                      <PacingRow segment={segment} result={result} index={index + 1} isLast={isLast} accumulatedDistance={accumulatedDistances[index]} isExpanded={isExpanded} onClick={() => handleToggle(segment.id)} bufferStatus={buffer?.status} data-id="element-920" />
                      {isExpanded && <PacingDetailPanel segment={segment} result={result} buffer={buffer} badges={badges} onUpdatePace={val => {
                  const p = parsePace(val);
                  if (p) updateSegmentPace(segment.id, p);
                }} onUpdateStop={mins => updateSegment(segment.id, {
                  stopTime: mins
                })} data-id="element-921" />}
                    </div>;
            })}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}